import { getTierForPsi, type AirTier } from '../api/tiers.ts';
import { REGION_IDS, type PsiData, type RegionId } from '../api/psi.ts';
import { formatReadingTime, type FeedStatus } from '../api/freshness.ts';
import type { Pun } from '../data/puns.ts';

export type ShareRegion = 'national' | RegionId;
export type ShareFormat = 'portrait' | 'square';
export interface ShareCardData {
  psi: number;
  pm25: number | null;
  tier: AirTier;
  punText: string;
  author: string;
  timestamp: string | null;
  regionLabel: string;
  stale: boolean;
}
export interface ShareSnapshot {
  readonly reading: Readonly<PsiData>;
  readonly punText: string;
  readonly author: string;
  readonly url: string;
  readonly previousDesign: boolean;
  readonly status: FeedStatus;
}
export const SHARE_REGIONS: readonly ShareRegion[] = ['national', ...REGION_IDS];

export function captureShareSnapshot(
  reading: PsiData,
  pun: Pun,
  url: string,
  previousDesign: boolean,
  status: FeedStatus,
): ShareSnapshot {
  const captured = Object.freeze({
    ...reading,
    reading: Object.freeze({
      ...reading.reading,
      tier: Object.freeze({ ...reading.reading.tier }),
    }),
    pm25ByRegion: Object.freeze({ ...reading.pm25ByRegion }),
    regions: Object.freeze(reading.regions.map((region) => Object.freeze({ ...region }))),
  });
  return Object.freeze({
    reading: captured as unknown as Readonly<PsiData>,
    punText: pun.text,
    author: pun.author || 'HazePun',
    url,
    previousDesign,
    status,
  });
}
export function selectShareReading(
  snapshot: ShareSnapshot,
  region: ShareRegion,
): ShareCardData | null {
  const reading = snapshot.reading;
  const psi =
    region === 'national'
      ? reading.reading.psi
      : reading.regions.find((entry) => entry.region === region)?.psi;
  if (psi === null || psi === undefined) return null;
  return {
    psi,
    pm25: region === 'national' ? reading.reading.pm25 : (reading.pm25ByRegion[region] ?? null),
    tier: getTierForPsi(psi),
    punText: snapshot.punText,
    author: snapshot.author,
    timestamp: reading.timestamp,
    regionLabel:
      region === 'national'
        ? 'Singapore · highest regional PSI'
        : `${region[0].toUpperCase() + region.slice(1)} Singapore`,
    stale: snapshot.status !== 'fresh',
  };
}
export function shareCaption(snapshot: ShareSnapshot, data: ShareCardData): string {
  return `💨 ${data.regionLabel}: PSI ${data.psi} (${data.tier.name}).\n${formatReadingTime(data.timestamp)}${data.stale ? ' · Delayed reading' : ''}\n“${data.punText}”\n\nLive Singapore PSI & puns: ${snapshot.url}`;
}
export function shareFilename(
  data: ShareCardData,
  region: ShareRegion,
  format: ShareFormat,
): string {
  const stamp =
    data.timestamp && Number.isFinite(Date.parse(data.timestamp))
      ? new Date(data.timestamp).toISOString().replace(/[:.]/g, '-')
      : 'time-unknown';
  return `hazepun-${region}-psi-${data.psi}-${stamp}-${format}.png`;
}
